import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  ArrowRight, 
  Building2, 
  TrendingUp, 
  PlusCircle, 
  Shield, 
  CheckCircle, 
  Users,
  Award,
  Clock,
  Star,
  Quote
} from 'lucide-react';
import NewsletterSignup from '../components/NewsletterSignup';
import VideoSection from '../components/VideoSection';

const Home = () => {
  const options = [
    {
      title: 'Bestehende GmbH kaufen',
      description:
        'Finde eine passende Bestands-GmbH und starte schneller mit einer bereits eingetragenen Gesellschaft.',
      icon: Building2,
      href: '/marktplatz',
      button: 'Zum Marktplatz',
      variant: 'outline',
      buttonClass:
        'border-2 border-blue-600 text-blue-600 hover:bg-blue-50 text-lg px-10 py-6',
    },
    {
      title: 'GmbH zum Verkauf anbieten',
      description:
        'Reiche deine Bestands-GmbH diskret ein. Wir prüfen die Angaben und bringen dich mit qualifizierten Interessenten zusammen.',
      icon: TrendingUp,
      href: '/seller-onboarding',
      button: 'Jetzt anbieten',
      variant: 'default',
      buttonClass:
        'bg-gradient-to-r from-orange-600 to-yellow-600 hover:from-orange-700 hover:to-yellow-700 text-white shadow-lg hover:shadow-xl transition-all text-lg px-10 py-6',
    },
    {
      title: 'Neue GmbH gründen',
      description:
        'Starte direkt das Formular. Wir begleiten dich von der Vorbereitung bis zur Eintragung – strukturiert und transparent.',
      icon: PlusCircle,
      href: '/onboarding',
      button: 'Formular starten',
      variant: 'default',
      buttonClass:
        'bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 text-white shadow-lg hover:shadow-xl transition-all text-lg px-10 py-6',
    },
  ];

  const stats = [
    { value: '150+', label: 'Vermittelte GmbHs', icon: Building2 },
    { value: '20+', label: 'Jahre Erfahrung', icon: Clock },
    { value: '92%', label: 'Kundenzufriedenheit', icon: Star },
    { value: '48h', label: 'Ø Reaktionszeit', icon: CheckCircle },
  ];

  const trustBadges = [
    { icon: Shield, text: 'SSL-verschlüsselt' },
    { icon: CheckCircle, text: 'DSGVO-konform' },
    { icon: Award, text: 'Geprüfte Qualität' },
    { icon: Users, text: 'Persönliche Beratung' },
  ];

  const testimonials = [
    {
      name: 'Michael K.',
      role: 'Geschäftsführer',
      location: 'München',
      text: 'Dank Firmbay konnte ich innerhalb von 5 Tagen eine passende Vorratsgesellschaft kaufen. Der Prozess war transparent und professionell.',
      rating: 5,
    },
    {
      name: 'Sandra M.',
      role: 'Unternehmerin',
      location: 'Hamburg',
      text: 'Hervorragende Beratung und schnelle Abwicklung. Ich habe meine GmbH-Gründung komplett über Firmbay abgewickelt - absolut empfehlenswert!',
      rating: 5,
    },
    {
      name: 'Thomas R.',
      role: 'Investor',
      location: 'Frankfurt',
      text: 'Als Investor habe ich bereits mehrere Gesellschaften über Firmbay erworben. Zuverlässig, diskret und fair.',
      rating: 5,
    },
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Firmbay | GmbH kaufen, verkaufen & gründen - Der Marktplatz für Vorratsgesellschaften</title>
        <meta name="description" content="GmbH kaufen oder Vorratsgesellschaft erwerben bei Firmbay. Bestands-GmbH sofort verfügbar. Professionelle Begleitung bei Kauf, Verkauf oder Neugründung Ihrer GmbH." />
        <meta name="keywords" content="GmbH kaufen, Vorratsgesellschaft kaufen, Mantelgesellschaft kaufen, GmbH gründen, Bestands-GmbH, GmbH verkaufen, GmbH Übernahme" />
      </Helmet>

      {/* Hero */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Firmbay
              <span className="block mt-2 bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
                Kaufen. Verkaufen. Gründen.
              </span>
            </h1>
            <p className="text-lg md:text-xl text-gray-600 mb-10 leading-relaxed">
              Wähle, was du vorhast – wir führen dich direkt zum passenden Prozess.
            </p>
          </div>

          {/* 3 Optionen */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
            {options.map((opt) => {
              const Icon = opt.icon;
              return (
                <Card
                  key={opt.title}
                  className="border-2 border-gray-200 hover:border-blue-200 hover:shadow-lg transition-all h-full"
                >
                  <CardContent className="pt-6 h-full flex flex-col">
                    <div className="w-12 h-12 rounded-xl bg-white shadow-sm border border-gray-200 flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-blue-700" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 mb-2">{opt.title}</h2>
                    <p className="text-sm text-gray-600 leading-relaxed mb-6 flex-1">{opt.description}</p>
                    <Link to={opt.href} className="inline-block w-full mt-auto">
                      <Button
                        size="lg"
                        variant={opt.variant}
                        className={`w-full justify-center ${opt.buttonClass}`}
                      >
                        {opt.button}
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Vertrauenssymbole */}
          <div className="mt-12 flex flex-wrap justify-center gap-6 md:gap-10">
            {trustBadges.map((badge, index) => {
              const Icon = badge.icon;
              return (
                <div key={index} className="flex items-center gap-2 text-gray-600">
                  <Icon className="w-5 h-5 text-green-600" />
                  <span className="text-sm font-medium">{badge.text}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Statistik-Sektion */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white border-y border-gray-100">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="text-center">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-blue-50 mb-4">
                    <Icon className="w-7 h-7 text-blue-600" />
                  </div>
                  <div className="text-3xl md:text-4xl font-bold text-gray-900 mb-1">{stat.value}</div>
                  <div className="text-sm text-gray-600">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Video-Sektion */}
      <VideoSection />

      {/* Testimonials */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Das sagen unsere Kunden
            </h2>
            <p className="text-gray-600">
              Vertrauen Sie auf die Erfahrungen zufriedener Unternehmer
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="bg-white border-0 shadow-md hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  {/* Sterne */}
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  
                  {/* Zitat */}
                  <div className="relative">
                    <Quote className="absolute -top-2 -left-2 w-8 h-8 text-blue-100" />
                    <p className="text-gray-700 leading-relaxed mb-4 pl-4">
                      "{testimonial.text}"
                    </p>
                  </div>

                  {/* Autor */}
                  <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-teal-500 flex items-center justify-center text-white font-semibold">
                      {testimonial.name.charAt(0)}
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{testimonial.name}</div>
                      <div className="text-sm text-gray-500">{testimonial.role}, {testimonial.location}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-14 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <NewsletterSignup />
        </div>
      </section>

      {/* Kleine Kontakt-Alternative */}
      <section className="py-14 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-gray-600 mb-6">
            Unsicher, welcher Weg der richtige ist? Schreib uns – wir helfen schnell weiter.
          </p>
          <Link to="/kontakt">
            <Button size="lg" className="bg-gray-900 hover:bg-gray-800 text-white text-lg px-10 py-6">
              Kontakt aufnehmen
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
